import { useEffect, useState } from "react";
import { ComposedChart, Line, ReferenceLine, Tooltip, XAxis, YAxis, ResponsiveContainer } from "recharts";
import { api, formatMoney } from "@/lib/api";

const DOT = { green: "#10b981", yellow: "#f59e0b", red: "#f43f5e" };

function Dot({ cx, cy, payload }) {
  if (cx == null || cy == null || payload?.roas == null) return null;
  return <circle cx={cx} cy={cy} r={2.6} fill={DOT[payload.light] || "#94a3b8"} stroke="white" strokeWidth={0.6} />;
}

function Tip({ active, payload, monthly }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] shadow-lg" data-testid="roas-spark-tooltip">
      <div className="font-semibold text-slate-900">{p.date}{monthly ? "(月)" : ""}</div>
      <div className="text-slate-600">營業額 {formatMoney(p.revenue, "TWD")}</div>
      <div className="text-slate-600">花費 {formatMoney(p.spend, "TWD")}</div>
      <div className="font-mono text-slate-900">ROAS {p.roas != null ? `${p.roas.toFixed(2)}x` : "—"}</div>
      {!monthly && p.ma7 != null && <div className="font-mono text-slate-500">7 日移動平均 {p.ma7.toFixed(2)}x</div>}
    </div>
  );
}

export default function RoasSparkline({ params, height = 64, testid = "roas-sparkline" }) {
  const [data, setData] = useState(null);
  const key = JSON.stringify(params || {});
  useEffect(() => {
    if (!params) return;
    let cancel = false;
    setData(null);
    api.get("/roas/daily", { params }).then((r) => { if (!cancel) setData(r.data); }).catch(() => { if (!cancel) setData({ mode: "none", points: [], note: "趨勢載入失敗" }); });
    return () => { cancel = true; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="h-4 text-[10px] text-slate-300" data-testid={`${testid}-loading`}>趨勢載入中…</div>;
  if (data.mode === "none" || !data.points?.length) {
    return (
      <div className="rounded border border-dashed border-slate-200 bg-slate-50 px-2 py-1.5 text-[10px] text-slate-400" data-testid={`${testid}-empty`}>
        {data.note || "接上 PMS 後顯示每日趨勢"}
      </div>
    );
  }
  const monthly = data.mode === "monthly";
  const maxY = Math.max(40, ...data.points.map((p) => Math.max(p.roas || 0, p.ma7 || 0)));
  return (
    <div data-testid={testid} data-mode={data.mode} data-points={data.points.length} style={{ minWidth: 80 }}>
      <ResponsiveContainer width="100%" height={height} minWidth={80} minHeight={height}>
        <ComposedChart data={data.points} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <XAxis dataKey="date" hide />
          <YAxis hide domain={[0, Math.ceil(maxY * 1.1)]} />
          <ReferenceLine y={20} stroke="#f43f5e" strokeDasharray="3 3" strokeOpacity={0.6} />
          <ReferenceLine y={35} stroke="#10b981" strokeDasharray="3 3" strokeOpacity={0.6} />
          <Tooltip content={<Tip monthly={monthly} />} cursor={{ stroke: "#cbd5e1" }} wrapperStyle={{ zIndex: 50 }} />
          <Line type="monotone" dataKey="roas" stroke="#94a3b8" strokeWidth={1} dot={<Dot />} activeDot={{ r: 4 }} isAnimationActive={false} />
          {!monthly && <Line type="monotone" dataKey="ma7" stroke="#0f766e" strokeWidth={1.8} dot={false} isAnimationActive={false} />}
        </ComposedChart>
      </ResponsiveContainer>
      <div className="flex items-center gap-2 text-[9px] text-slate-400 mt-0.5 flex-wrap">
        {monthly ? <span data-testid={`${testid}-monthly-note`}>手填只有月資料，顯示月 ROAS</span> : <><span className="inline-block w-3 h-[2px] bg-teal-700" />7 日移動平均</>}
        <span>虛線 20x / 35x</span>
        {data.note && !monthly && <span>{data.note}</span>}
      </div>
    </div>
  );
}
